import { BIOMES, type BiomeId, type RealmPlace, type RealmRefusal } from '@goodtime/shared';

/**
 * The realm map's side places and secrets (milestone 2): where each sits on
 * the painting, as fractions (0 to 1) of its width and height like the
 * landmarks, and what the map says about it. What each costs and pays comes
 * from the server (RealmView); nothing here sets a price.
 */

export type SpotId = 'fountain' | 'springs' | 'lighthouse' | 'bridge' | 'oasis';

/** A side place: a pin over its spot on the painting. */
export interface RealmSpot {
  id: SpotId;
  /** The name on its pin and its card. The server's `springs` is shown as
   *  The Warm Pool (a Frostpeak slot is called Hot Springs). */
  name: string;
  /** Where the pin points, as fractions of the painting. */
  x: number;
  y: number;
  /** The world it is in: its pin's plate and its card sit on that world's
   *  floor. */
  biome: BiomeId;
  /** The card's line under the picture. */
  blurb: string;
}

export const REALM_SPOTS: readonly RealmSpot[] = [
  { id: 'fountain', name: 'Wishing Fountain', x: 0.443, y: 0.45, biome: 'kingdom', blurb: 'Toss a coin and make a wish.' },
  // On Greendale's grass, though the Dustwood name is nearer.
  { id: 'springs', name: 'The Warm Pool', x: 0.418, y: 0.595, biome: 'kingdom', blurb: "Put your feet up. There's a gift every hour." },
  // Over the lamp: the lighthouse itself is Stormy Seas, whose plate hangs below it.
  { id: 'lighthouse', name: 'The Lighthouse', x: 0.064, y: 0.41, biome: 'cove', blurb: 'The ships leave parcels for the keeper.' },
  { id: 'bridge', name: 'The Troll Bridge', x: 0.326, y: 0.488, biome: 'kingdom', blurb: 'The troll hides his coins under a cup.' },
  // Over the pool: the camels by it are Desert Caravan, whose plate hangs below them.
  { id: 'oasis', name: 'The Oasis', x: 0.703, y: 0.832, biome: 'egypt', blurb: 'There are coins buried in the sand.' },
];

/** A secret: something glinting on the painting. What it pays is the
 *  server's. */
export interface SecretSpot {
  id: string;
  x: number;
  y: number;
}

/** One in each world, two in Greendale. */
export const SECRET_SPOTS: readonly SecretSpot[] = [
  { id: 's1', x: 0.345, y: 0.335 }, // Greendale: the sheep on the hill
  { id: 's2', x: 0.078, y: 0.655 }, // Saltspray Cove: the starfish on the sand
  { id: 's3', x: 0.446, y: 0.209 }, // Frostpeak: in the snow (the reindeer that stood here is gone)
  { id: 's4', x: 0.704, y: 0.426 }, // Wonderfizz: the pink lake
  { id: 's5', x: 0.872, y: 0.53 }, // Neon Heights: the lamp by the steps
  { id: 's6', x: 0.872, y: 0.855 }, // Sunscar Sands: the little pyramid
  { id: 's7', x: 0.595, y: 0.84 }, // Dustwood: the cart of gold
  { id: 's8', x: 0.16, y: 0.5 }, // Greendale: by the river (the deer that stood here are gone)
];

export function spot(id: string): RealmSpot | undefined {
  return REALM_SPOTS.find((s) => s.id === id);
}

/** The world a side place is in (index into BIOMES). */
export function spotWorld(s: RealmSpot): number {
  return Math.max(
    0,
    BIOMES.findIndex((b) => b.id === s.biome),
  );
}

/** Coins as the game writes them: 2,500. */
export function coinsText(n: number): string {
  return Math.round(n).toLocaleString('en-GB');
}

/** A wait, short: "42m", "1h 5m", "5h". Never under a minute. */
export function waitText(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** A wait in words: "in 42 minutes", "in about 2 hours". */
export function waitWords(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) return minutes === 1 ? 'in a minute' : `in ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? 'in about an hour' : `in about ${hours} hours`;
}

/**
 * Where a place stands now:
 * - `locked` until opened;
 * - `ready` when there's something to do (a wish, a gift, today's game);
 * - `waiting` while the warm pool's next gift isn't ready;
 * - `done` when today's uses are used up.
 */
export type SpotState = 'locked' | 'ready' | 'waiting' | 'done';

export function spotState(p: RealmPlace, now: number): SpotState {
  if (!p.open) return 'locked';
  if (p.kind === 'game' && p.playId) return 'ready';
  if (p.playsLeft !== undefined && p.playsLeft <= 0) return 'done';
  if (p.readyAt !== undefined && p.readyAt > now) return 'waiting';
  return 'ready';
}

/** The pin's short status: the price while locked, the wait, or nothing. */
export function pinStatus(p: RealmPlace, now: number): string {
  switch (spotState(p, now)) {
    case 'locked':
      return coinsText(p.cost);
    case 'waiting':
      return waitText((p.readyAt ?? now) - now);
    default:
      return '';
  }
}

/** What a place does, in one line, from the server's numbers (the card's
 *  one-tap explanation). */
export function rulesLine(p: RealmPlace): string {
  const perDay = p.playsPerDay ?? 1;
  switch (p.kind) {
    case 'wish':
      return `A wish costs ${coinsText(p.price ?? 100)} coins and always pays some back, sometimes a gem. ${perDay} wishes a day.`;
    case 'rest':
      return `${coinsText(p.gift ?? 50)} coins an hour, ${perDay} times a day.`;
    default:
      return perDay === 1 ? 'One free game every day.' : `${perDay} free games every day.`;
  }
}

/** The card's line about now: what's left today, or when to come back. */
export function statusLine(p: RealmPlace, now: number, dayEndsAt?: number): string {
  const state = spotState(p, now);
  const tomorrow = dayEndsAt && dayEndsAt > now ? ` Back ${waitWords(dayEndsAt - now)}.` : ' Back tomorrow.';
  if (state === 'locked') return `Opens for ${coinsText(p.cost)} coins, once.`;
  if (state === 'done') {
    return (p.kind === 'wish' ? 'No wishes left today.' : p.kind === 'rest' ? "That's all the gifts for today." : "Today's game is played.") + tomorrow;
  }
  if (state === 'waiting') return `The next gift is ready ${waitWords((p.readyAt ?? now) - now)}.`;
  if (p.kind === 'wish') {
    const left = p.playsLeft ?? 0;
    return left === 1 ? 'One wish left today.' : `${left} wishes left today.`;
  }
  if (p.kind === 'rest') return 'A gift is ready.';
  return p.playId ? 'Your game is waiting for you.' : "Today's game is ready.";
}

/** Thrown by the app's realm calls when the server turns a request down
 *  (402 or 409 with a reason). */
export class RealmRefused extends Error {
  constructor(readonly refusal: RealmRefusal) {
    super(`realm: ${refusal.error}`);
  }
}

/** What to tell the player about a refusal. */
export function refusalLine(r: RealmRefusal, now: number): string {
  switch (r.error) {
    case 'NOT_ENOUGH_COINS':
      return r.cost !== undefined && r.coins !== undefined
        ? `You need ${coinsText(r.cost - r.coins)} more coins. Spin in a slot for more.`
        : 'You need more coins. Spin in a slot for more.';
    case 'REALM_WAIT':
      return `Not yet: the next gift is ready ${waitWords((r.readyAt ?? now) - now)}.`;
    case 'REALM_DONE_TODAY':
      return "That's all for today. Come back tomorrow.";
    case 'PLACE_CLOSED':
      return "This place isn't open yet.";
    default:
      return "That didn't work. Try again in a moment.";
  }
}
